import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import siteData from '../data/siteData.json';
import api from '../api/client';
import { activateAdminMode } from '../hooks/useAdminMode';
import './Footer.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

export default function Footer() {
  const [company, setCompany] = useState(siteData.company);
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/company').then(setCompany).catch(() => {});
  }, []);

  useEffect(() => {
    if (showModal) setTimeout(() => inputRef.current?.focus(), 50);
  }, [showModal]);

  const openModal = (e) => {
    e.preventDefault();
    setShowModal(true);
    setPassword('');
    setError(null);
  };

  const closeModal = () => setShowModal(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '비밀번호가 올바르지 않습니다.');
      activateAdminMode(data.token);
      setShowModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="footer">
      <div className="footer__top">
        <div className="container footer__grid">
          <div className="footer__brand">
            <div className="footer__logo">
              <img src="/logo.png" alt="주식회사 허브" className="footer__logo-img" />
              <div>
                <div className="footer__logo-kr">{company.name}</div>
                <div className="footer__logo-en">{company.nameEn}</div>
              </div>
            </div>
            <p className="footer__slogan">{company.slogan}</p>
          </div>

          <div className="footer__links">
            <h4>빠른 메뉴</h4>
            <ul>
              <li><Link to="/">홈</Link></li>
              <li><Link to="/about">회사소개</Link></li>
              <li><Link to="/services">사업영역</Link></li>
              <li><Link to="/careers">채용정보</Link></li>
              <li><Link to="/contact">고객센터</Link></li>
            </ul>
          </div>

          <div className="footer__links">
            <h4>서비스</h4>
            <ul>
              <li><Link to="/services">행사 지원</Link></li>
              <li><Link to="/services">헤드헌팅·채용대행</Link></li>
              <li><Link to="/services">생산·물류·도급</Link></li>
              <li><Link to="/contact">견적 신청</Link></li>
              <li><Link to="/careers">채용 공고</Link></li>
            </ul>
          </div>

          <div className="footer__contact">
            <h4>연락처</h4>
            <ul>
              <li>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.06 2.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                <a href={`tel:${company.phone}`}>{company.phone}</a>
              </li>
              <li>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <a href={`mailto:${company.email}`}>{company.email}</a>
              </li>
              <li>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>{company.address}</span>
              </li>
              <li>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span>{company.business_hours || company.businessHours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="footer__bottom">
        <div className="container footer__bottom-inner">
          <p>© {new Date().getFullYear()} {company.name}. All rights reserved.</p>
          <div className="footer__legal">
            <Link to="/privacy">개인정보처리방침</Link>
            <Link to="/terms">이용약관</Link>
            <button onClick={openModal} className="footer__admin-link footer__admin-btn">관리자</button>
          </div>
        </div>
      </div>
      {showModal && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal">
            <h3 className="admin-modal__title">관리자 모드 활성화</h3>
            <p className="admin-modal__desc">비밀번호 확인 후 홈페이지 편집 모드가 켜집니다.</p>
            {error && <div className="admin-modal__error">{error}</div>}
            <form onSubmit={handleLogin}>
              <input
                ref={inputRef}
                type="password"
                className="admin-modal__input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="관리자 비밀번호"
              />
              <div className="admin-modal__actions">
                <button type="button" className="admin-modal__cancel" onClick={closeModal}>취소</button>
                <button type="submit" className="admin-modal__confirm" disabled={loading}>
                  {loading ? '확인 중…' : '확인'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
}
